import { Combobox as ComboboxPrimitive } from '@base-ui/react/combobox'
import { CheckIcon, ChevronDownIcon, XIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useId, useLayoutEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '@/shared/lib'
import type { ComboboxOption } from '@/shared/ui/combobox'
import { FieldOutline } from '@/shared/ui/field-outline'
import { FloatingLabel } from '@/shared/ui/floating-label'
import { useVisibleBackground } from '@/shared/ui/use-visible-background'

type MultiSelectProps = {
  label: string
  options: ComboboxOption[]
  value: string[]
  onValueChange: (value: string[]) => void
  placeholder?: string
  hint?: ReactNode
  selectAllLabel?: string
  clearLabel?: string
  /**
   * Show each option's wire value after its label, in the list only.
   *
   * For a vocabulary whose codes the reader already thinks in — countries and
   * languages — where the names alone are not always distinct: `ak` and `tw`
   * are both «акан», and `nb`, `nn` and `no` are three kinds of Norwegian. It
   * stays out of the chips, out of the sort key and out of the `+N` fold, which
   * is what folding the code into the label itself would have cost.
   *
   * Off for a vocabulary whose code is its label — `Desktop desktop` reads as a
   * mistake.
   */
  hasCodes?: boolean
  /**
   * `md` is the 44px form field. `sm` is the 32px toolbar field, where the
   * label degrades to a placeholder because there is no room for it to float —
   * the same ladder `Combobox` uses, so a toolbar can mix the two.
   */
  size?: 'sm' | 'md'
  className?: string
}

const CHIP =
  'inline-flex h-6 shrink-0 items-center gap-1.25 rounded-full border border-primary/26 bg-primary/14 text-caption text-accent-text'
const GAP = 6
/** The caret needs somewhere to live, or the field stops being typable. */
const INPUT_MIN = 48

/**
 * Multiple choice in one field, typed into rather than scrolled through.
 *
 * The field *is* the input, the same bargain `Combobox` makes: chips sit in it,
 * a caret sits after them, and the list narrows as the letters land. That is
 * what makes 249 countries a usable option list — a plain scroll pane is not.
 *
 * The label line carries the count («Метки — 2 из 5») because the chips
 * themselves can be cut off, and the panel opens with select-all / clear so a
 * long list never needs N clicks to reset.
 *
 * **Deviation from the design system.** Verdant's own MultiSelect wraps its
 * chips onto as many lines as they need (`flex-wrap`, `height: auto`,
 * `min-height: 44`). This one stays on the single 44px line and folds whatever
 * does not fit into a trailing `+N` chip, so a row of fields keeps one height.
 */
export function MultiSelect({
  label,
  options,
  value,
  onValueChange,
  placeholder,
  hint,
  selectAllLabel,
  clearLabel,
  hasCodes = false,
  size = 'md',
  className,
}: MultiSelectProps) {
  const { t } = useTranslation('common')
  const id = useId()
  const hintId = `${id}-hint`

  /*
   * Filtering is ours, not Base UI's internal pass, for two reasons: a country
   * is as likely to be reached by its code as by its name, and «Выбрать все»
   * has to mean "everything the query left" — which needs the matching set in
   * hand, not just in the list.
   */
  const { contains } = ComboboxPrimitive.useFilter()
  const [query, setQuery] = useState('')
  // Only so the background can be put back — see `useVisibleBackground`.
  const [isOpen, setIsOpen] = useState(false)
  useVisibleBackground(isOpen)

  const matches = (option: ComboboxOption, search: string) => {
    const trimmed = search.trim()
    if (trimmed === '') return true
    return (
      contains(option.label, trimmed) ||
      option.value.toLowerCase().startsWith(trimmed.toLowerCase())
    )
  }

  const matching = options.filter((option) => matches(option, query))

  // Option objects, not codes: Base UI compares items by reference, and a
  // reference out of `options` matches without a custom comparer.
  const selected = options.filter((option) => value.includes(option.value))

  // The label carries the count only once there is one. Repeating "nothing
  // selected" in the label and again in the field says the same thing twice.
  const labelText =
    value.length === 0
      ? label
      : `${label} — ${t('ui.selectedOf', { count: value.length, total: options.length })}`

  // `sm` has no floating label above it, so the field itself has to say what it
  // is — the same fallback `Combobox` makes at that size.
  const emptyText =
    placeholder ?? (size === 'sm' ? label : t('ui.nothingSelected'))

  const rowRef = useRef<HTMLDivElement>(null)
  const mirrorRef = useRef<HTMLSpanElement>(null)
  const [visibleCount, setVisibleCount] = useState(selected.length)

  /*
   * The visible row is clipped, so it cannot be measured against itself —
   * hiding a chip would free the space that made it not fit and the count would
   * oscillate. An invisible mirror holding every chip plus the widest counter
   * gives stable widths to measure against.
   */
  const fingerprint = value.join(' ')
  useLayoutEffect(() => {
    const row = rowRef.current
    const mirror = mirrorRef.current
    if (row === null || mirror === null) return

    const measure = () => {
      const nodes = [...mirror.children] as HTMLElement[]
      const counter = nodes.pop()
      if (counter === undefined) return

      // The input is not optional furniture: a field with no room to type in is
      // this component's whole point removed.
      const available = row.clientWidth - INPUT_MIN
      let used = 0
      let fits = 0

      for (const [index, chip] of nodes.entries()) {
        const width = used + (index === 0 ? 0 : GAP) + chip.offsetWidth
        const isLast = index === nodes.length - 1
        const reserve = isLast ? 0 : GAP + counter.offsetWidth
        if (width + reserve > available) break
        used = width
        fits += 1
      }

      // Always keep one chip: a field showing only "+5" says nothing.
      setVisibleCount(nodes.length === 0 ? 0 : Math.max(fits, 1))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => {
      observer.disconnect()
    }
  }, [fingerprint, options])

  const shown = selected.slice(0, visibleCount)
  const hiddenCount = selected.length - shown.length

  /*
   * Typing and reviewing are two jobs and the 44px line only fits one. With a
   * few chips in it the caret is squeezed into whatever is left — a text box
   * the width of a word. So a query hands the whole line to the input and folds
   * the selection down to a single count; releasing it gives the chips back.
   *
   * Focus alone does not do this: opening the list is not typing, and a field
   * that reshuffles on click reads as a glitch.
   */
  const isTyping = query !== ''

  const counterChip = <span className={cn(CHIP, 'px-2 font-mono')}>+{selected.length}</span>

  return (
    <ComboboxPrimitive.Root
      multiple
      items={options}
      filter={matches}
      value={selected}
      onValueChange={(next: ComboboxOption[]) => {
        onValueChange(next.map((option) => option.value))
        // Otherwise the field keeps a stale fragment sitting next to the chip
        // it just produced.
        setQuery('')
      }}
      inputValue={query}
      onInputValueChange={setQuery}
      onOpenChange={(next) => {
        setIsOpen(next)
        if (!next) setQuery('')
      }}
    >
      <div className={cn('flex flex-col gap-1.5', className)}>
        <FieldFrame size={size} label={labelText}>
          <ComboboxPrimitive.Chips
            ref={rowRef}
            className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden"
          >
            {isTyping ? (
              selected.length > 0 && (
                <span className={cn(CHIP, 'px-2 font-mono')}>{selected.length}</span>
              )
            ) : (
              <>
                {/* Only the chips that fit are rendered, and `ChipRemove` works
                    off DOM position — which stays aligned because the ones that
                    fit are always the leading slice of the selection. */}
                {shown.map((option) => (
                  <ComboboxPrimitive.Chip
                    key={option.value}
                    aria-label={option.label}
                    className={cn(
                      CHIP,
                      'py-0 pr-1 pl-2.25 outline-none focus-within:bg-primary/24',
                    )}
                  >
                    {option.label}
                    <ComboboxPrimitive.ChipRemove
                      aria-label={`${t('ui.remove')} ${option.label}`}
                      className="flex size-4 items-center justify-center rounded-full outline-none hover:bg-primary/20"
                    >
                      <XIcon strokeWidth={2} className="size-2" />
                    </ComboboxPrimitive.ChipRemove>
                  </ComboboxPrimitive.Chip>
                ))}

                {hiddenCount > 0 && (
                  <span className={cn(CHIP, 'px-2 font-mono')}>+{hiddenCount}</span>
                )}
              </>
            )}

            <ComboboxPrimitive.Input
              id={id}
              aria-label={label}
              aria-describedby={hint === undefined ? undefined : hintId}
              placeholder={selected.length > 0 ? '' : emptyText}
              className="h-6 min-w-12 flex-1 bg-transparent pl-1 text-body outline-none placeholder:text-fg-disabled"
            />
          </ComboboxPrimitive.Chips>

          <span
            ref={mirrorRef}
            aria-hidden
            className="pointer-events-none invisible absolute flex gap-1.5 whitespace-nowrap"
          >
            {selected.map((option) => (
              <span key={option.value} className={cn(CHIP, 'py-0 pr-1 pl-2.25')}>
                {option.label}
                <span className="flex size-4 items-center justify-center rounded-full">
                  <XIcon strokeWidth={2} className="size-2" />
                </span>
              </span>
            ))}
            {counterChip}
          </span>

          <ComboboxPrimitive.Trigger
            aria-label={label}
            className="group/trigger absolute top-1/2 right-1.5 flex size-6 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground transition-colors outline-none hover:text-foreground"
          >
            <ComboboxPrimitive.Icon
              render={
                <ChevronDownIcon
                  strokeWidth={1.6}
                  className="size-icon-xs transition-[rotate] duration-[180ms] ease-out group-data-popup-open/trigger:rotate-180"
                />
              }
            />
          </ComboboxPrimitive.Trigger>
        </FieldFrame>

        {hint !== undefined && (
          <div id={hintId} className="text-caption text-muted-foreground">
            {hint}
          </div>
        )}
      </div>

      <ComboboxPrimitive.Portal>
        <ComboboxPrimitive.Positioner align="start" sideOffset={6} className="isolate z-50">
          <ComboboxPrimitive.Popup className="max-h-(--available-height) w-(--anchor-width) origin-(--transform-origin) overflow-hidden rounded-xl border border-border-strong bg-overlay p-1.5 backdrop-blur-glass inset-shadow-glass shadow-popover">
            {/* `onMouseDown` is prevented so the caret never leaves the input:
                the popup closes on focus loss, and a select-all that shuts the
                panel is a select-all you cannot follow with a second look. */}
            <div className="mb-1.5 flex items-center gap-2 border-b border-border-hairline px-2 pt-1 pb-2">
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onValueChange(matching.map((option) => option.value))}
                className="text-caption text-accent-text press"
              >
                {selectAllLabel ?? t('ui.selectAll')}
              </button>
              <span aria-hidden className="h-3 w-px bg-border-strong" />
              <button
                type="button"
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => onValueChange([])}
                className="text-caption text-fg-secondary press"
              >
                {clearLabel ?? t('ui.clear')}
              </button>
              <span className="ml-auto font-mono text-label text-muted-foreground">
                {value.length}
              </span>
            </div>

            {/* Stays mounted whatever the list holds: it is a live region, and
                screen readers only announce a change if the node persists. */}
            <ComboboxPrimitive.Empty className="px-2.5 py-2 text-body text-muted-foreground empty:hidden">
              {t('ui.nothingFound')}
            </ComboboxPrimitive.Empty>

            {/* Reserved for the same reason as `Combobox`: 249 countries filter
                down to a handful as the letters land, crossing `max-h` in both
                directions, and an unreserved gutter changes every option's
                width on the keystroke that crosses it. */}
            <ComboboxPrimitive.List className="max-h-44 scroll-py-1 overflow-x-hidden overflow-y-auto outline-none [scrollbar-gutter:stable]">
              {(option: ComboboxOption) => (
                <ComboboxPrimitive.Item
                  key={option.value}
                  value={option}
                  className="group/item flex w-full cursor-pointer items-center gap-2.25 rounded-md px-2.5 py-2 text-left text-body text-fg-body transition-colors outline-hidden select-none data-highlighted:bg-surface-2 data-highlighted:text-foreground"
                >
                  <span
                    aria-hidden
                    className="flex size-4 shrink-0 items-center justify-center rounded-[5px] border border-veil/18 bg-surface-sunken group-data-selected/item:border-primary group-data-selected/item:bg-primary group-data-selected/item:text-primary-foreground"
                  >
                    <ComboboxPrimitive.ItemIndicator>
                      <CheckIcon strokeWidth={2.2} className="size-2.5" />
                    </ComboboxPrimitive.ItemIndicator>
                  </span>
                  <span className="flex-1 truncate">{option.label}</span>
                  {/* Not `aria-hidden`: on the rows this exists for it is the
                      only thing telling two identically named options apart, so
                      hiding it would leave a screen reader with the ambiguity
                      the code is here to remove. */}
                  {hasCodes && (
                    <span className="shrink-0 font-mono text-caption text-fg-disabled">
                      {option.value}
                    </span>
                  )}
                </ComboboxPrimitive.Item>
              )}
            </ComboboxPrimitive.List>
          </ComboboxPrimitive.Popup>
        </ComboboxPrimitive.Positioner>
      </ComboboxPrimitive.Portal>
    </ComboboxPrimitive.Root>
  )
}


/**
 * The box around the chips: a 44px form field with a notched outline and a
 * floating label, or a 32px toolbar field with a plain border and none of that
 * apparatus. Same split as `Combobox`, and it is layout only — every Base UI
 * part stays where it was.
 */
function FieldFrame({
  size,
  label,
  children,
}: {
  size: 'sm' | 'md'
  label: string
  children: ReactNode
}) {
  if (size === 'sm') {
    return (
      <ComboboxPrimitive.InputGroup className="relative flex h-control-sm w-full cursor-text items-center gap-1.5 rounded-lg border border-border-strong bg-surface-sunken py-1 pr-8 pl-2 transition-colors focus-within:border-ring">
        {children}
      </ComboboxPrimitive.InputGroup>
    )
  }

  return (
    <div className="group/field relative">
      <ComboboxPrimitive.InputGroup className="relative flex h-control-lg w-full cursor-text items-center gap-1.5 rounded-lg bg-surface-sunken py-1.5 pr-8.5 pl-2 transition-colors">
        <FloatingLabel isRaised>{label}</FloatingLabel>
        {children}
      </ComboboxPrimitive.InputGroup>

      <FieldOutline label={label} isRaised className="border-border-strong" />
    </div>
  )
}
